import { FolderOpenFilled } from '@ant-design/icons'
import { Tree } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Modal, Trans } from '@/components'

import * as fsOps from '../fs-operations'
import type { FSContext, IFile } from '../types'

export interface MoveDialogProps {
  onCancel: () => void
  onOk: () => void
  files: IFile[]
  currentPath?: string
}

interface DataNode {
  title: string
  key: string
  file: IFile | undefined
  isLeaf?: boolean
  children?: DataNode[]
  icon?: React.ReactNode
  disabled?: boolean
}

const MoveDialogCore = ({
  files,
  onCancel,
  onOk,
  domain,
  context,
  currentPath,
}: MoveDialogProps & { domain: number; context: FSContext }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [treeData, setTreeData] = useState<DataNode[]>([])
  const [selectedDir, setSelectedDir] = useState<IFile | undefined>()

  // Normalize path for comparison (ensure trailing slash except for root)
  const normalizePath = useCallback((path: string) => {
    if (!path || path === '/') return '/'
    return path.endsWith('/') ? path : `${path}/`
  }, [])

  // Check if a directory should be disabled as move target
  // Disabled when:
  // 1. It's the current path (where user is browsing)
  // 2. It's one of the files being moved (can't move into itself)
  // 3. It's a subdirectory of any file being moved (can't move parent into child)
  const shouldDisableNode = useCallback(
    (nodePath: string, filesToMove: IFile[]): boolean => {
      const normalizedNodePath = normalizePath(nodePath)

      // Check against current browsing path
      if (currentPath && normalizedNodePath === normalizePath(currentPath)) {
        return true
      }

      // Check against each file being moved
      for (const file of filesToMove) {
        if (!file.isDir) continue // Only folders need this check

        const normalizedFilePath = normalizePath(file.path)

        // Can't move folder into itself
        if (normalizedNodePath === normalizedFilePath) {
          return true
        }

        // Can't move folder into its own subdirectory
        if (normalizedNodePath.startsWith(normalizedFilePath)) {
          return true
        }
      }

      return false
    },
    [currentPath, normalizePath],
  )

  const getDirections = useCallback(
    async (path: string): Promise<DataNode[]> => {
      return fsOps.ls(context, { path, domain }).then(({ files: dirFiles }) => {
        return dirFiles
          .filter((info: any) => info.isDir)
          .map((it: any) => {
            return {
              title: it.name,
              key: it.name,
              file: it,
              icon: <FolderOpenFilled style={{ color: 'orange', fontSize: 20 }} />,
              disabled: shouldDisableNode(it.path, files),
            } as DataNode
          })
      })
    },
    [domain, context, files, shouldDisableNode],
  )

  const makeNode = useCallback((parent: DataNode, children: DataNode[]) => {
    parent.children = children?.map((it) => {
      return {
        ...it,
        key: `${parent.key}${it.key}/`,
      }
    })
    return parent
  }, [])

  const findNode = useCallback((root: DataNode, key: string): DataNode | null => {
    if (root.key == key) {
      return root
    } else {
      const children = root.children || []
      for (let i = 0; i < children.length; i++) {
        const found = findNode(children[i], key)
        if (found) return found
      }
      return null
    }
  }, [])

  useEffect(() => {
    getDirections('/').then((treeData) => {
      const root = {
        title: '/',
        key: '/',
        file: {
          path: '/',
        },
        icon: <FolderOpenFilled style={{ color: 'orange', fontSize: 20 }} />,
        disabled: shouldDisableNode('/', files),
      } as DataNode
      setTreeData([makeNode(root, treeData)])
    })
  }, [getDirections, makeNode, files, shouldDisableNode])

  const onLoadData = useCallback(
    (item: DataNode) => {
      return new Promise<void>((resolve, reject) => {
        getDirections(item.key)
          .then((_treeData: DataNode[]) => {
            for (let i = 0; i < treeData.length; i++) {
              const found = findNode(treeData[i], item.key)
              if (found) {
                makeNode(found, _treeData)
                setTreeData([...treeData])
                break
              }
            }
            resolve()
          })
          .catch(reject)
      })
    },
    [getDirections, findNode, makeNode, treeData],
  )

  const _onConfirm = useCallback(async () => {
    if (files.length > 0 && selectedDir) {
      const getTarget = (path: string, dirname: string) => {
        // Ensure dirname has trailing slash (except for root)
        const normalizedDir =
          dirname === '/' ? '/' : dirname.endsWith('/') ? dirname : `${dirname}/`

        const parts = path.split('/')
        if (parts.reverse()[0] == '') {
          return `${normalizedDir}${parts[1]}/`
        } else {
          return `${normalizedDir}${parts[0]}`
        }
      }
      setLoading(true)
      try {
        await Promise.all(
          files.map((file) =>
            fsOps.mv(context, {
              path: file.path,
              target: getTarget(file.path, selectedDir.path),
              domain,
            }),
          ),
        )
        setSelectedDir(undefined)
        onOk()
      } catch (error) {
        setLoading(false)
      }
    }
  }, [files, selectedDir, onOk, domain, context])

  return (
    <Modal open={files.length > 0} onClose={onCancel}>
      <Modal.Header>{t('data.moveModal.title')}</Modal.Header>
      <Modal.Body>
        <div style={{ maxHeight: window.innerHeight * 0.7, overflowY: 'auto' }}>
          {treeData.length ? (
            <Tree
              multiple={false}
              onSelect={(_: React.Key[], { node }) => {
                setSelectedDir(node.file)
              }}
              defaultExpandedKeys={['/']}
              showIcon
              loadData={onLoadData}
              treeData={treeData}
            />
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onCancel}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={_onConfirm}
          disabled={loading || !selectedDir || shouldDisableNode(selectedDir.path, files)}
        >
          {loading ? (
            <Trans i18nKey="common.confirmModalOkLoading">Processing...</Trans>
          ) : (
            <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export function useMoveCore(context: FSContext, domain: number) {
  return {
    MoveDialog: useCallback(
      (props: MoveDialogProps) => <MoveDialogCore {...props} domain={domain} context={context} />,
      [domain, context],
    ),
  }
}
